/*
 * Shared UI kit gallery shell. Used in two ways:
 * 1) Standalone Vite page: /kit-gallery.html (dev only; mounts when #kit-gallery-root exists)
 * 2) In-app route: /dev/components via KitGalleryPage (superadmin Dev Tools)
 *
 * Component demos are grouped by purpose into Category modules under ./kit-gallery/*.
 * The fixed GalleryNav (title + category chips) links to each category.
 *
 * Query params (standalone): ?theme=light, ?modal=1
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import Button from '../components/ui/Button';
import SegmentedControl from '../components/ui/SegmentedControl';
import { GalleryNav } from './kit-gallery/galleryPrimitives';
import ActionsGallery from './kit-gallery/ActionsGallery';
import FormsGallery from './kit-gallery/FormsGallery';
import DataDisplayGallery from './kit-gallery/DataDisplayGallery';
import OverlaysGallery from './kit-gallery/OverlaysGallery';
import BrandGallery from './kit-gallery/BrandGallery';
import DashboardGallery from './kit-gallery/DashboardGallery';
import MobileTestCardsGallery from './kit-gallery/MobileTestCardsGallery';
import '../App.css';

const CATEGORIES = [
  { id: 'actions', label: 'Actions' },
  { id: 'forms', label: 'Forms & inputs' },
  { id: 'data-display', label: 'Data display' },
  { id: 'overlays', label: 'Overlays & surfaces' },
  { id: 'brand', label: 'Brand & logos' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'mobile-test-cards', label: 'Mobile test cards' },
];

export function Gallery({ embedded = false } = {}) {
  const params = new URLSearchParams(window.location.search);
  const [theme, setTheme] = useState(() => {
    if (embedded) {
      return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
    }
    return params.get('theme') === 'light' ? 'light' : 'dark';
  });
  const [modal, setModal] = useState(!embedded && params.get('modal') === '1');

  const applyTheme = (t) => {
    setTheme(t);
    document.documentElement.setAttribute('data-theme', t);
  };

  return (
    <div
      style={{
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        minHeight: embedded ? undefined : '100vh',
        padding: embedded ? '0 0 1.5rem' : '0 2rem 1.5rem',
      }}
    >
      <GalleryNav categories={CATEGORIES} embedded={embedded}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-h1)' }}>FieldCert UI kit — components &amp; buttons</h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button variant="ghost" size="sm" onClick={() => setModal(true)}>Open Modal</Button>
            {!embedded && (
              <SegmentedControl
                ariaLabel="Theme"
                value={theme}
                onChange={applyTheme}
                options={[{ value: 'dark', label: 'Dark' }, { value: 'light', label: 'Light' }]}
              />
            )}
          </div>
        </header>
      </GalleryNav>

      <ActionsGallery />
      <FormsGallery />
      <DataDisplayGallery />
      <OverlaysGallery modal={modal} onModalChange={setModal} />
      <BrandGallery />
      <DashboardGallery />
      <MobileTestCardsGallery />
    </div>
  );
}

// Standalone entry only — App imports Gallery without mounting here.
const standaloneRoot = document.getElementById('kit-gallery-root');
if (standaloneRoot) {
  const params = new URLSearchParams(window.location.search);
  const initialTheme = params.get('theme') === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', initialTheme);
  // MemoryRouter: section-card rows contain real <Link>s; do not navigate away.
  ReactDOM.createRoot(standaloneRoot).render(
    <React.StrictMode>
      <MemoryRouter>
        <Gallery />
      </MemoryRouter>
    </React.StrictMode>,
  );
}
